import { Suspense, type ReactNode } from "react";
import { createBrowserRouter } from "react-router-dom";
import App from "@/App";
import { ErrorBoundary } from "@/components/general/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { LoadingSpinner } from "@/components/general/LoadingSpinner";
import { lazyRetry } from "@/lib/lazyRetry";
import { YIELD_ASSET_PICKER_PATH } from "@/lib/yieldPaths";

const HomePage = lazyRetry(() => import("@/screens/home-page"));
const SignupPage = lazyRetry(() => import("@/screens/signup-page"));
const LoginPage = lazyRetry(() => import("@/screens/login-page"));
const ForgotPasswordPage = lazyRetry(
  () => import("@/screens/forgot-password-page"),
);
const ResetPasswordPage = lazyRetry(
  () => import("@/screens/reset-password-page"),
);
const WalletPage = lazyRetry(() => import("@/screens/wallet-page"));
const YieldAssetPickerPage = lazyRetry(
  () => import("@/screens/yield-asset-picker-page"),
);
const YieldIntroPage = lazyRetry(() => import("@/screens/yield-intro-page"));
const YieldCreateFlexiblePage = lazyRetry(
  () => import("@/screens/yield-create-flexible-page"),
);
const ValidatorPage = lazyRetry(() => import("@/screens/validator-page"));
const ValidatorDetailsPage = lazyRetry(
  () => import("@/screens/validator-details-page"),
);
const RedeemPage = lazyRetry(() => import("@/screens/redeem-page"));
const UnstakePage = lazyRetry(() => import("@/screens/unstake-page"));
const MoveStakePage = lazyRetry(() => import("@/screens/move-stake-page"));
const HistoryPage = lazyRetry(() => import("@/screens/history-page"));
const LearnPage = lazyRetry(() => import("@/screens/learn-page"));
const LearnDetailPage = lazyRetry(() => import("@/screens/learn-detail-page"));
const ForecastPage = lazyRetry(() => import("@/screens/forecast-page"));
const EarnPage = lazyRetry(() => import("@/screens/earn-page"));
const CampaignPage = lazyRetry(() => import("@/screens/campaign-page"));
const PerksPage = lazyRetry(() => import("@/screens/perks-page"));
const PortfolioPage = lazyRetry(() => import("@/screens/portfolio-page"));
const PortfolioSummaryPage = lazyRetry(
  () => import("@/screens/portfolio-summary-page"),
);
const PortfolioPositionsPage = lazyRetry(
  () => import("@/screens/portfolio-positions-page"),
);
const EarningsBreakdownPage = lazyRetry(
  () => import("@/screens/earnings-breakdown-page"),
);
const LeaderboardPage = lazyRetry(() => import("@/screens/leaderboard-page"));
const DashboardPage = lazyRetry(() => import("@/screens/dashboard-page"));
const ProfilePage = lazyRetry(() => import("@/screens/profile-page"));
const NotificationsPage = lazyRetry(
  () => import("@/screens/notifications-page"),
);
const AccountsPage = lazyRetry(() => import("@/screens/accounts-page"));
const WalletTransferFormPage = lazyRetry(
  () => import("@/screens/wallet-transfer-form-page"),
);
const WalletUnlockedWithdrawPage = lazyRetry(
  () => import("@/screens/wallet-unlocked-withdraw-page"),
);
const AccountSettingsPage = lazyRetry(
  () => import("@/screens/account-settings-page"),
);
const PersonalDetailsPage = lazyRetry(
  () => import("@/screens/personal-details-page"),
);
const PreferencesSettingsPage = lazyRetry(
  () => import("@/screens/preferences-settings-page"),
);
const SecuritySettingsPage = lazyRetry(
  () => import("@/screens/security-settings-page"),
);
const RecipientSettingsPage = lazyRetry(
  () => import("@/screens/recipient-settings-page"),
);
const KycPage = lazyRetry(
  () => import("@/screens/kyc-page"),
);
const InviteFriendsPage = lazyRetry(
  () => import("@/screens/invite-friends-page"),
);
const BlogPage = lazyRetry(() => import("@/screens/blog-page"));
const BlogDetailPage = lazyRetry(() => import("@/screens/blog-detail-page"));
const LisarGrowthPage = lazyRetry(() => import("@/screens/lisar-growth-page"));
const LisarSavingsPage = lazyRetry(
  () => import("@/screens/lisar-savings-page"),
);
const LisarFlexPage = lazyRetry(
  () => import("@/screens/lisar-flex-page"),
);
const PrivacyPolicyPage = lazyRetry(
  () => import("@/screens/privacy-policy-page"),
);
const TermsOfUsePage = lazyRetry(() => import("@/screens/terms-of-use-page"));
const FlexCardPage = lazyRetry(() => import("@/screens/flex-card-page"));
const WelcomePage = lazyRetry(() => import("@/screens/welcome-page"));
const NotFoundPage = lazyRetry(() => import("@/screens/NotFoundPage"));

const withSuspense = (element: ReactNode) => (
  <Suspense fallback={<LoadingSpinner />}>{element}</Suspense>
);

const withErrorBoundary = (element: ReactNode) => (
  <ErrorBoundary>{element}</ErrorBoundary>
);

const withSuspenseAndErrorBoundary = (element: ReactNode) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>{element}</Suspense>
  </ErrorBoundary>
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    ),
    children: [
      // Public routes
      { index: true, element: withSuspenseAndErrorBoundary(<HomePage />) },
      { path: "login", element: withSuspenseAndErrorBoundary(<LoginPage />) },
      { path: "signup/:referralCode?", element: withSuspenseAndErrorBoundary(<SignupPage />) },

      {
        path: "dashboard",
        element: withSuspenseAndErrorBoundary(<DashboardPage />),
      },
      {
        path: "forgot-password",
        element: withSuspenseAndErrorBoundary(<ForgotPasswordPage />),
      },
      {
        path: "reset-password",
        element: withSuspenseAndErrorBoundary(<ResetPasswordPage />),
      },
      { path: "blog", element: withSuspenseAndErrorBoundary(<BlogPage />) },
      {
        path: "blog/:slug",
        element: withSuspenseAndErrorBoundary(<BlogDetailPage />),
      },
      {
        path: "lisar-growth",
        element: withSuspenseAndErrorBoundary(<LisarGrowthPage />),
      },
      {
        path: "lisar-savings",
        element: withSuspenseAndErrorBoundary(<LisarSavingsPage />),
      },
      {
        path: "lisar-flex",
        element: withSuspenseAndErrorBoundary(<LisarFlexPage />),
      },
      {
        path: "privacy-policy",
        element: withSuspenseAndErrorBoundary(<PrivacyPolicyPage />),
      },
      {
        path: "terms-of-use",
        element: withSuspenseAndErrorBoundary(<TermsOfUsePage />),
      },

      // Protected routes
      {
        path: "wallet",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<WalletPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/yields/intro",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<YieldIntroPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: YIELD_ASSET_PICKER_PATH.replace(/^\//, ""),
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<YieldAssetPickerPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/yields/create-flexible",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<YieldCreateFlexiblePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/:mode/:asset",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<WalletTransferFormPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/unlocked-withdraw/:walletType",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<WalletUnlockedWithdrawPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/:walletType",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<WalletPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "welcome",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<WelcomePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "validator",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<ValidatorPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "validator-details/:validatorId",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<ValidatorDetailsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "redeem",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<RedeemPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "unstake/:validatorId",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<UnstakePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "move-stake",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<MoveStakePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "history",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<HistoryPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "learn",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<LearnPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "learn/:slug",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<LearnDetailPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "forecast",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<ForecastPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "earn",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<EarnPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "campaign",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<CampaignPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "perks",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PerksPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "earn/flex-card",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<FlexCardPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "portfolio",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PortfolioPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "portfolio/summary",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PortfolioSummaryPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "portfolio/positions",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PortfolioPositionsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "wallet/returns",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<EarningsBreakdownPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "leaderboard",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<LeaderboardPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "profile",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<ProfilePage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "notifications",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<NotificationsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "accounts",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<AccountsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/account",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<AccountSettingsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/account/personal-details",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PersonalDetailsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/recipients",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<RecipientSettingsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/preferences",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<PreferencesSettingsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/security",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<SecuritySettingsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/kyc",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<KycPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "settings/invite-friends",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<InviteFriendsPage />)}
          </ProtectedRoute>
        ),
      },
      {
        path: "invite-friends",
        element: (
          <ProtectedRoute>
            {withSuspenseAndErrorBoundary(<InviteFriendsPage />)}
          </ProtectedRoute>
        ),
      },
      { path: "*", element: withSuspenseAndErrorBoundary(<NotFoundPage />) },
    ],
  },
]);
